<template>
  <div>
    <div fluid class="px-4 pt-2">
      <v-row>
        <v-col :cols="12" class="client-profile-col">
          <v-card
            v-if="!selectedClient && !isCreating"
            :dark="isDarkTheme"
            class="pa-6 text-center elevation-4"
          >
            <v-icon size="64" :style="getGradientStyle"
              >mdi-account-check</v-icon
            >
            <p class="mt-4 text-h6">
              Click on a {{ getClientLabel.toLowerCase() }} from the list to
              Select or click 'Add New' to create one.
            </p>
          </v-card>

          <v-card v-else :dark="isDarkTheme" class="pa-2 elevation-4">
            <v-toolbar flat :style="{ background: getThemeColor }" dark>
              <v-toolbar-title>{{
                isCreating ? "New " + getClientLabel : selectedClient.name
              }}</v-toolbar-title>
              <v-spacer></v-spacer>

              <v-btn
                v-if="!isCreating"
                icon
                @click="deleteConfirmDialog = true"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
              <v-btn icon @click="cancelAction">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>

            <v-tabs v-model="activeTab" :color="getThemeColor" grow>
              <v-tab>3. Payments</v-tab>
              <v-tab>4. Invoices</v-tab>
              <v-tab>5. {{ getTrustAccLabel }} Account</v-tab>
              <v-tab>6. Profile & Edit</v-tab>
              <v-tab>7. Insurance</v-tab>
              <v-tab v-if="getAppMode === 'legal'">8. Cases</v-tab>
              <v-tab v-if="clientIsMechanics">8. Vehicles</v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
              <!-- Payments -->
              <v-tab-item>
                <InvoicePaymentsSummary
                  :profile="profile"
                ></InvoicePaymentsSummary>
              </v-tab-item>

              <!-- Invoices -->
              <v-tab-item>
                <v-card-text>
                  <h3 class="mb-3">
                    Total Sum of Invoices
                    <strong style="color: red"
                      >{{ getCurrency
                      }}{{ totalAmountOfAllInvoices(clientInvoices) }}</strong
                    >
                    [{{ clientInvoices.length }}
                    {{ clientInvoices.length < 2 ? "Invoice" : "Invoices" }}]
                  </h3>

                  <InvoiceTable
                    :invoices="clientInvoices"
                    @refresh-needed="refreshClientInvoicesData"
                  ></InvoiceTable>

                  <v-divider class="my-4"></v-divider>
                </v-card-text>
              </v-tab-item>

              <!-- Trust Account -->
              <v-tab-item>
                <TrustAccountLedger
                  scope="client"
                  :reference="selectedClient.client_reference"
                ></TrustAccountLedger>
              </v-tab-item>

              <!-- Profile -->
              <v-tab-item>
                <v-card-text>
                  <v-form ref="profileForm" v-model="validForm">
                    <v-row>
                      <v-col cols="12" sm="3" class="text-center">
                        <v-avatar
                          size="128"
                          color="grey lighten-3"
                          class="mb-3"
                        >
                          <img
                            v-if="selectedClient.image"
                            :src="selectedClient.image"
                            alt="Client Image"
                          />
                          <v-icon v-else size="80">mdi-account</v-icon>
                        </v-avatar>
                        <v-file-input
                          label="Upload Image"
                          dense
                          outlined
                          prepend-icon="mdi-camera"
                          @change="onFileChange"
                          accept="image/*"
                        ></v-file-input>
                      </v-col>
                      <v-col cols="12" sm="9">
                        <v-text-field
                          v-model="selectedClient.name"
                          :label="`${getClientLabel} Full Names`"
                          outlined
                          required
                          @change="
                            updateModelSingleRow(
                              'clients',
                              'name',
                              selectedClient.id,
                              selectedClient.name
                            )
                          "
                        ></v-text-field>
                        <v-text-field
                          v-model="selectedClient.client_reference"
                          label="Client Reference"
                          outlined
                          readonly
                        ></v-text-field>
                        <v-text-field
                          v-model="selectedClient.id_number"
                          label="Client ID Number / Business Registration Number"
                          outlined
                          @change="
                            updateModelSingleRow(
                              'clients',
                              'id_number',
                              selectedClient.id,
                              selectedClient.id_number
                            )
                          "
                        ></v-text-field>
                        <v-text-field
                          v-model="selectedClient.email"
                          label="Email Address"
                          outlined
                          type="email"
                          @change="
                            updateModelSingleRow(
                              'clients',
                              'email',
                              selectedClient.id,
                              selectedClient.email
                            )
                          "
                        ></v-text-field>
                        <v-text-field
                          v-model="selectedClient.phone"
                          label="Primary Phone"
                          outlined
                          @change="
                            updateModelSingleRow(
                              'clients',
                              'phone',
                              selectedClient.id,
                              selectedClient.phone
                            )
                          "
                        ></v-text-field>
                        <v-text-field
                          v-model="selectedClient.address"
                          label="Primary Address"
                          outlined
                          @change="
                            updateModelSingleRow(
                              'clients',
                              'address',
                              selectedClient.id,
                              selectedClient.address
                            )
                          "
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-divider class="my-3"></v-divider>
                    <h4 class="text-subtitle-1">Additional Details</h4>
                    <v-row>
                      <v-col cols="12" sm="4"
                        ><v-text-field
                          v-model="selectedClient.dob"
                          label="Date of Birth (DOB)"
                          outlined
                          placeholder="YYYY-MM-DD"
                          @change="
                            updateModelSingleRow(
                              'clients',
                              'dob',
                              selectedClient.id,
                              selectedClient.dob
                            )
                          "
                        ></v-text-field
                      ></v-col>
                      <v-col cols="12" sm="4"
                        ><v-select
                          v-model="selectedClient.gender"
                          :items="['Male', 'Female', 'Other']"
                          label="Gender"
                          outlined
                          @change="
                            updateModelSingleRow(
                              'clients',
                              'gender',
                              selectedClient.id,
                              selectedClient.gender
                            )
                          "
                        ></v-select
                      ></v-col>
                      <v-col cols="12" sm="4"
                        ><v-text-field
                          v-model="selectedClient.alt_contact"
                          label="Alternative Contact"
                          outlined
                          @change="
                            updateModelSingleRow(
                              'clients',
                              'alt_contact',
                              selectedClient.id,
                              selectedClient.alt_contact
                            )
                          "
                        ></v-text-field
                      ></v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6"
                        ><v-text-field
                          v-model="selectedClient.institution"
                          label="Institution / Employer / Business Name"
                          outlined
                          @change="
                            updateModelSingleRow(
                              'clients',
                              'institution',
                              selectedClient.id,
                              selectedClient.institution
                            )
                          "
                        ></v-text-field
                      ></v-col>
                      <v-col cols="12" sm="6"
                        ><v-text-field
                          v-model="selectedClient.position"
                          label="Position/Title"
                          outlined
                          @change="
                            updateModelSingleRow(
                              'clients',
                              'position',
                              selectedClient.id,
                              selectedClient.position
                            )
                          "
                        ></v-text-field
                      ></v-col>
                      <v-col cols="12">
                        <v-textarea
                          v-model="selectedClient.notes"
                          label="Add/Edit Client Notes"
                          outlined
                          @change="
                            updateModelSingleRow(
                              'clients',
                              'notes',
                              selectedClient.id,
                              selectedClient.notes
                            )
                          "
                        ></v-textarea>
                      </v-col>
                    </v-row>
                  </v-form>
                </v-card-text>
                <v-card-actions class="d-flex justify-end">
                  <v-btn
                    :color="getThemeColor"
                    :disabled="!validForm"
                    dark
                    @click="handleSaveClient"
                    >{{ isCreating ? "Create Client" : "Save Changes" }}</v-btn
                  >
                </v-card-actions>
              </v-tab-item>

              <!-- Cases -->
              <v-tab-item v-if="getAppMode === 'legal'">
                <v-card-text>
                  <h3 class="mb-3">
                    Active Cases/Files (Total: {{ clientCases.length }})
                  </h3>
                  <v-data-table
                    :headers="caseHeaders"
                    :items="clientCases"
                    class="elevation-1"
                  ></v-data-table>
                </v-card-text>
              </v-tab-item>

              <!-- Automotive Mechanic -->
              <v-tab-item v-if="clientIsMechanics">
                <v-card-text>
                  <h3 class="mb-3">
                    Registered Vehicles (Total: {{ sampleVehicles.length }})
                  </h3>
                  <v-data-table
                    :headers="vehicleHeaders"
                    :items="sampleVehicles"
                    class="elevation-1"
                  ></v-data-table>
                </v-card-text>
              </v-tab-item>

              <!-- Insurances -->
              <v-tab-item>
                <v-card-text>
                  <h3 class="mb-3">
                    Known Insurance Schemes/Companies (Total:
                    {{ clientInsurances.length }})
                  </h3>
                  <v-card>
                    <v-card-title>
                      Insurance Policies
                      <v-spacer></v-spacer>
                      <v-btn color="primary" @click="openInsuranceDialog()"
                        >Add Insurance</v-btn
                      >
                    </v-card-title>

                    <v-data-table
                      :headers="insuranceHeaders"
                      :items="clientInsurances"
                      :loading="loading"
                      class="elevation-1"
                    >
                      <template v-slot:item.premium_amount="{ item }">
                        {{ formatCurrency(item.premium_amount) }}
                      </template>

                      <template v-slot:item.actions="{ item }">
                        <v-icon
                          small
                          class="mr-2"
                          color="blue"
                          @click="editInsurance(item)"
                        >
                          mdi-pencil
                        </v-icon>
                        <v-icon
                          small
                          color="red"
                          @click="deleteInsurance(item.id)"
                        >
                          mdi-delete
                        </v-icon>
                      </template>
                    </v-data-table>

                    <DataEntryAndEdit
                      table="client_insurances"
                      :row="insuranceForm"
                      :value="insuranceDialog"
                      @close="closeInsuranceDialog"
                      @saved="insuranceSavedOrUpdated"
                    ></DataEntryAndEdit>
                  </v-card>
                </v-card-text>
              </v-tab-item>
            </v-tabs-items>

            <v-dialog v-model="deleteConfirmDialog" max-width="500">
              <v-card>
                <v-card-title class="headline error white--text"
                  >Confirm Deletion</v-card-title
                >
                <v-card-text class="pt-4">
                  Are you sure you want to permanently delete **{{
                    selectedClient.name
                  }}**? This action cannot be undone.
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue" text @click="deleteConfirmDialog = false"
                    >Cancel</v-btn
                  >
                  <v-btn color="error" text @click="confirmDelete"
                    >Delete</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-card>
        </v-col>
      </v-row>

      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        timeout="3000"
      >
        {{ snackbar.message }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false"
            >Close</v-btn
          >
        </template>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
import axios from "@/plugins/axios";
import helpersJs from "@/plugins/helpers";
import companiesJs from "@/plugins/companies";
import DataEntryAndEdit from "@/modules/CrudDatabases/DataEntryAndEdit.vue";
import InvoiceTable from "@/modules/InvoicesAndQuotes/InvoiceTable.vue";
import TrustAccountLedger from "@/modules/TrustAccount/TrustAccountLedger.vue";
import InvoicePaymentsSummary from "@/modules/Payments/InvoicePaymentsSummary.vue";
import Quotations from "@/modules/InvoicesAndQuotes/Quotations.vue";

export default {
  name: "ClientManagement",
  props: {
    selectedClientProp: {
      type: Object,
      default: () => null,
    },
  },
  components: {
    DataEntryAndEdit,
    InvoiceTable,
    TrustAccountLedger,
    InvoicePaymentsSummary,
    Quotations,
  },
  data: () => ({
    clientsTable: "clients",
    userCompany: null,
    // UI Navigation & Workspace State
    selectedClient: null,
    isCreating: false,
    search: "",
    activeTab: 0,
    validForm: true,
    deleteConfirmDialog: false,
    insuranceDialog: false,
    loading: false,
    saving: false,

    // Profile Workspace State
    profile: {
      client: {},
      invoices: [],
      quotations: [],
      cases: [],
      insurance: [],
      totals: {},
    },

    // Sub-form state
    insuranceForm: {
      id: null,
      client_reference: "",
      provider_name: "",
      scheme_name: "",
      policy_number: "",
      dependent_code: "00",
      principal_member_name: "",
      status: "Active",
      auth_number: "",
      premium_amount: 0.0,
      benefit_cap_amount: null,
      valid_from: "",
      valid_to: "",
      notes: "",
    },

    // App Data Store
    clients: [],

    // Detail Workspace Analytics Fallbacks
    clientInvoices: [],
    clientPayments: [],
    clientCases: [],
    sampleVehicles: [],
    insuranceStatuses: ["Active", "Suspended", "Lapsed", "Pending"],
    clientInsurances: [],
    // Global Notification Config
    snackbar: { show: false, message: "", color: "" },
  }),

  computed: {
    getCurrency() {
      return this.$store.getters.getCurrency;
    },
    getCurrentUser() {
      return this.$store.getters.getCurrentUser;
    },
    getGradientStyle() {
      return this.$store.getters.getGradientStyle;
    },
    getAppMode() {
      return this.$store.getters.getAppMode;
    },
    getTrustAccLabel() {
      return this.$store.getters.getTrustAccLabel;
    },
    isMobile() {
      return this.$store.getters.isMobile;
    },
    getAccessToken() {
      return this.$store.getters.getAccessToken;
    },
    getThemeColor() {
      return this.$store.getters.getThemeColor;
    },
    getPrimaryColor() {
      return this.$store.getters.getPrimaryColor;
    },
    getCompanyLabels() {
      return this.$store.getters.getCompanyLabels || {};
    },
    getClientLabel() {
      return this.getCompanyLabels?.client || "Client";
    },
    isDarkTheme() {
      return false;
    },

    formTitle() {
      return this.insuranceForm.id
        ? "Edit Insurance Policy"
        : "Add Insurance Policy";
    },

    // Datatable Headers config
    listHeaders() {
      return [
        { text: "Name/ID", value: "name" },
        { text: "Phone", value: "phone", align: "end", sortable: false },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ];
    },
    invoiceHeaders() {
      return [
        { text: "Invoice No.", value: "invoice_number" },
        { text: "Date", value: "invoice_date" },
        { text: "Amount", value: "amount", align: "end" },
        { text: "Due Date", value: "due_date", align: "center" },
      ];
    },

    caseHeaders() {
      return [
        { text: "Court Case No.", value: "courtCaseNo" },
        { text: "SAPS Case No", value: "sapsCaseNo" },
        { text: "Case Type", value: "case_type" },
        { text: "Defendant", value: "defendantName" },
        { text: "Status", value: "status" },
      ];
    },
    vehicleHeaders() {
      return [
        { text: "Make", value: "make" },
        { text: "Model", value: "model" },
        { text: "VIN", value: "VIN" },
      ];
    },
    insuranceHeaders() {
      return [
        { text: "Provider", value: "provider_name" },
        { text: "Scheme", value: "scheme_name" },
        { text: "Policy No.", value: "policy_number" },
        { text: "Principal", value: "principal_member_name" },
        { text: "Status", value: "status" },
        { text: "Amount", value: "premium_amount" },
        { text: "Benefit", value: "benefit_cap_amount" },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ];
    },

    // Aggregations
    billingSummary() {
      const totalInvoiced = this.clientInvoices.reduce(
        (sum, inv) => sum + (inv.amount || 0),
        0
      );
      const totalPaid = this.clientPayments.reduce(
        (sum, pay) => sum + (pay.amount || 0),
        0
      );
      const balanceOutstanding = totalInvoiced - totalPaid;

      return {
        totalInvoiced,
        totalPaid,
        balanceOutstanding: balanceOutstanding > 0 ? balanceOutstanding : 0,
      };
    },
    clientIsMedicalLegal() {
      return ["Patient", "Client"].includes(this.getClientLabel);
    },
    clientIsMechanics() {
      return this.getClientLabel === "Customer";
    },
  },

  watch: {
    selectedClientProp(newVal) {
      if (newVal) {
        this.selectClient(newVal);
      }
    },
    isCreating(newVal) {
      if (newVal) {
        this.selectedClient = this.getNewClientModel();
        this.profile = {
          client: this.selectedClient,
          invoices: [],
          quotations: [],
          cases: [],
          insurance: [],
          totals: {},
        };
        this.clientInvoices = [];
        this.clientPayments = [];
        this.clientCases = [];
        this.sampleVehicles = [];
        this.clientInsurances = [];
        this.activeTab = 0;
      }
    },
    activeTab(newVal) {
      // Define which indexes are restricted (e.g., anything greater than 1)
      const freeTabsLimit = 1;

      if (newVal > freeTabsLimit) {
        this.checkPackageStatus();
      }
    },
  },

  async mounted() {
    await this.initialiseClients();
    const compData = await companiesJs.getCompanyWithEmis(
      this.getCurrentUser.emis
    );
    this.userCompany = compData?.company || null;
  },

  methods: {
    checkPackageStatus() {
      if (!this.userCompany || this.userCompany.package === "freemium") {
        this.$store.commit("setUpgradeDialog", true);

        this.$nextTick(() => {
          this.activeTab = 0;
        });
        return;
      }
    },
    async initialiseClients() {
      if (this.selectedClientProp) {
        await this.selectClient(this.selectedClientProp);
      } else {
        const storedClient =
          JSON.parse(localStorage.getItem("selectedClient")) || null;
        if (storedClient) {
          await this.selectClient(storedClient);
        }
      }
      await this.getAllClients();
    },
    async getAllClients() {
      this.$store.commit("setLoader", true);
      try {
        const clients = await axios.getData(
          "/auth/get-all-clients",
          this.getAccessToken
        );
        if (clients) this.clients = clients;
      } catch (err) {
        this.showSnackbar("Failed to fetch clients list", "error");
      } finally {
        this.$store.commit("setLoader", false);
      }
    },
    async fetchClientProfile(reference) {
      if (!reference) return;
      this.loading = true;
      try {
        const response = await axios.postDataWithToken(
          `/auth/get-client-profile`,
          { clientReference: reference },
          this.getAccessToken
        );
        if (response && response.client) {
          this.profile = response;
          const clientData = response.client || null;
          if (!clientData) {
            this.$toast.error("Client not found!");
            return;
          }
          localStorage.setItem("selectedClient", JSON.stringify(clientData));
          this.selectedClient = clientData;

          // Populate sub-datasets required by tabs
          this.clientInvoices = response.invoices || [];
          this.clientPayments = response.payments || [];
          this.clientCases = response.cases || [];
          this.sampleVehicles = response.vehicles || [];
          this.clientInsurances = response.insurances || [];
        } else {
          this.showSnackbar("Client profile payload not found.", "orange");
        }
      } catch (error) {
        console.error("Error loading profile", error);
        this.showSnackbar("Error loading client profile data.", "error");
      } finally {
        this.loading = false;
      }
    },
    // In your Parent Component methods:
    async refreshClientInvoicesData() {
      if (this.selectedClient?.client_reference) {
        // Ensure fetchClientProfile re-assigns clientInvoices as a brand new array reference
        await this.fetchClientProfile(this.selectedClient.client_reference);
      }
    },
    totalAmountOfAllInvoices(clientInvoices) {
      if (!clientInvoices || clientInvoices.length < 1) return 0;
      const total = clientInvoices.reduce(
        (acc, invoice) =>
          acc + parseFloat(invoice.calculation?.total || invoice.amount || 0),
        0
      );
      return helpersJs.formatMoney(total);
    },
    async refreshClientList() {
      this.cancelAction();
      await this.getAllClients();
    },
    async updateModelSingleRow(table, column, id, value) {
      if (!table || !id) {
        return false;
      }
      this.$store.commit("setLoader", true);
      await helpersJs.updateModelSingleRow(table, column, id, value);
      this.$store.commit("setLoader", false);
      this.$toast.success("Data updated successfully!", { duration: 5000 });
      this.refreshClientAfterUpdate();
    },
    refreshClientAfterUpdate() {
      if (!this.clients || this.clients.length < 1 || !this.selectedClient) {
        return false;
      }
      const clientIndex = this.clients.findIndex(
        (client) => client.id == this.selectedClient.id
      );

      if (clientIndex !== -1) {
        this.$set(
          this.clients,
          clientIndex,
          JSON.parse(JSON.stringify(this.selectedClient))
        );
      }
    },
    async handleSaveClient() {
      const client = this.selectedClient;
      const isNew = this.isCreating;

      if (!isNew) {
        this.$toast.success("Data updated successfully!", { duration: 5000 });
        return false;
      }

      this.$store.commit("setLoader", true);
      try {
        const response = await axios.postDataWithToken(
          "/auth/save-or-update-client",
          { client },
          this.getAccessToken
        );

        if (response && response.status === "success") {
          const savedClient = response.client || client;
          if (isNew) {
            this.clients.push(savedClient);
            this.selectClient(savedClient);
            this.isCreating = false;
            this.showSnackbar(
              `${this.getClientLabel} created successfully!`,
              "success"
            );
          } else {
            const index = this.clients.findIndex((c) => c.id === client.id);
            if (index !== -1) {
              this.$set(this.clients, index, savedClient);
              this.selectClient(savedClient);
            }
            this.showSnackbar(
              `${this.getClientLabel} updated successfully!`,
              "success"
            );
          }
        } else {
          this.showSnackbar(
            "Oops, Something Went Wrong. Consult System Admin",
            "error"
          );
        }
      } catch (err) {
        this.showSnackbar(
          "Network communication error executing save routine.",
          "error"
        );
      } finally {
        this.$store.commit("setLoader", false);
      }
    },
    async confirmDelete() {
      this.deleteConfirmDialog = false;
      const clientId = this.selectedClient.id;
      const clientName = this.selectedClient.name;

      this.$store.commit("setLoader", true);
      try {
        await helpersJs.deleteSingleRow(this.clientsTable, clientId);
        this.clients = this.clients.filter((c) => c.id !== clientId);
        this.showSnackbar(
          `${this.getClientLabel} ${clientName} deleted successfully.`,
          "success"
        );
        this.cancelAction();
      } catch (err) {
        this.showSnackbar("Failed to clean database record row.", "error");
      } finally {
        this.$store.commit("setLoader", false);
      }
    },

    // Insurance Sub-Workspace Features
    openInsuranceDialog() {
      this.resetInsuranceForm();
      this.insuranceDialog = true;
    },
    insuranceSavedOrUpdated(insurance) {
      const insuranceIndex = this.clientInsurances.findIndex(
        (insur) => insur.id == insurance.id
      );
      if (insuranceIndex !== -1) {
        this.$set(this.clientInsurances, insuranceIndex, insurance);
      } else {
        this.clientInsurances.unshift(insurance);
      }
    },
    closeInsuranceDialog() {
      this.insuranceDialog = false;
      this.resetInsuranceForm();
    },
    resetInsuranceForm() {
      this.insuranceForm = {
        id: null,
        client_reference: this.selectedClient?.client_reference || "",
        provider_name: "",
        scheme_name: "",
        policy_number: "",
        dependent_code: "00",
        principal_member_name: this.selectedClient?.name || "",
        status: "Active",
        auth_number: "",
        premium_amount: 0.0,
        benefit_cap_amount: null,
        valid_from: "",
        valid_to: "",
        notes: "",
      };
    },
    editInsurance(item) {
      this.insuranceForm = JSON.parse(JSON.stringify(item));
      this.insuranceDialog = true;
    },
    async deleteInsurance(id) {
      if (!confirm("Are you sure you want to delete this insurance record?"))
        return;

      try {
        await helpersJs.deleteSingleRow("client_insurances", id);
        this.clientInsurances = this.clientInsurances.filter(
          (i) => i.id !== id
        );
        this.showSnackbar("Record deleted.", "success");
      } catch (err) {
        this.showSnackbar("Delete operation failed.", "error");
      }
    },

    // Presentation Utilities
    onFileChange(file) {
      if (!file) {
        this.selectedClient.image = null;
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        this.selectedClient.image = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    async selectClient(client) {
      this.isCreating = false;
      if (!client) return;

      this.selectedClient = client;
      localStorage.setItem("selectedClient", JSON.stringify(client));
      this.activeTab = 0;

      // Fetch full profile data including invoices, payments, and cases
      if (client.client_reference) {
        await this.fetchClientProfile(client.client_reference);
      }
    },
    startCreateNew() {
      this.selectedClient = this.getNewClientModel();
      this.isCreating = true;
    },
    cancelAction() {
      this.selectedClient = null;
      this.isCreating = false;
      localStorage.removeItem("selectedClient");
    },
    formatCurrency(amount) {
      return this.getCurrency + "" + helpersJs.formatMoney(amount);
    },
    showSnackbar(message, color) {
      this.snackbar.message = message;
      this.snackbar.color = color || "info";
      this.snackbar.show = true;
    },
    getNewClientModel() {
      return {
        id: null,
        name: "",
        client_reference: "",
        id_number: "",
        institution: null,
        position: null,
        dob: null,
        gender: null,
        email: null,
        alt_contact: null,
        phone: null,
        address: "",
        city: "",
        postal_code: "",
        province: null,
        notes: null,
        emis: null,
        image: null,
      };
    },
  },
};
</script>

<style scoped>
/* Master-Detail Columns on desktop */
@media (min-width: 960px) {
  .client-list-col {
    max-width: 350px;
    flex: 0 0 350px;
    transition: all 0.3s;
  }
  .client-profile-col {
    flex-grow: 1;
    transition: all 0.3s;
  }
}
/* Ensure the detail view takes full width on mobile/when list is hidden */
@media (max-width: 959px) {
  .client-profile-col {
    width: 100%;
  }
}
</style>
